import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { GetWSMgr, GetDoc } from "@/Utils/StateLib";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";
import { Globals } from "@coreapp/CoreApp/Globals";
import { isModeBackedByRuntime, isValidCreateParam, isValidModeForRoute } from "@coreapp/constants";

/**
 * Centralized URL security gate. Runs on every location change and normalizes
 * the URL against role, UI profile, and access-group permissions (all sourced
 * from usePermissionCheck + existing coreapp managers — no new permission
 * logic is introduced here).
 *
 * The URL is the source of truth for app state, so cold-start deep links
 * and pasted URLs can put the app into edit/planning/settings/boxlayout/etc.
 * directly. The URL layer must enforce the same gates the UI does.
 *
 * Forbidden query params are stripped via replace-nav (no history pollution).
 * Path-level permission enforcement lives in <PermissionGate> at the route
 * definitions. Mounted once in AppLayout.
 *
 * Known limitation: React effects fire bottom-up, so in the render cycle that
 * first observes a forbidden URL, child providers' URL-sync effects may run
 * against the pre-stripped value before this gate's strip commits. The
 * replace-nav triggers a second render where the forbidden param is gone; the
 * observable impact is a one-frame flicker of the forbidden state (e.g. a
 * sidebar briefly painting "Background" before settling). Acceptable because
 * (a) this is a secondary defense layer behind component-level permission
 * checks and (b) the stripped URL prevents any persistent/bookmarked forbidden
 * state. If flicker becomes a problem, lift the gate above the router.
 */
export const useUrlSecurityGate = (): void => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const perms = usePermissionCheck();
  // Re-fire the gate when the doc/template state changes. A cold-start
  // URL with ?view= runs the gate before templates hydrate — canAccessView
  // can't distinguish "loading" from "missing" at that moment and defers
  // (returns true). Depending on `chartDocument` triggers a re-check once
  // SignalDocChanged / template-load fires. Same pattern as
  // ViewManagerProvider / useTopOfChartSync / useBackgroundSync.
  // `?? {}` makes the gate tolerant of test wrappers that don't mount a
  // ChartDocumentProvider — at the cost of losing the template-load
  // re-fire in those tests, which only matters in jsdom, not production.
  const { chartDocument } = useChartDocumentContext() ?? {};

  useEffect(() => {
    try {
      // Wait for login + doc so permission checks have real inputs.
      if (!(Globals as unknown as { _loginComplete?: boolean })._loginComplete) return;
      const doc = GetDoc();
      if (!doc) return;

      const pathname = location.pathname;

      // Path-level permission enforcement moved to PermissionGate at
      // route definitions (Root.tsx). This gate handles query-level
      // enforcement only — strip forbidden params.

      // ---- QUERY-LEVEL (strip forbidden params) ----
      const updates: Record<string, null> = {};

      const mode = searchParams.get("mode");
      const tool = searchParams.get("tool");
      const view = searchParams.get("view");
      const create = searchParams.get("create");
      const chartExplorer = searchParams.has("chartexplorer");
      const display = searchParams.get("display");

      if (
        mode === "edit" &&
        (!perms.canAccessEdit || (create === "true" && !perms.canEditDocuments))
      ) {
        updates.mode = null;
      }
      if (mode === "planning" && !perms.canAccessPlanning) {
        updates.mode = null;
      }
      if (mode === "reports" && !perms.canAccessReporting) {
        updates.mode = null;
      }

      if (create === "true" && !perms.canEditDocuments) {
        updates.create = null;
      }

      if (chartExplorer && !perms.canAccessChartExplorer) {
        updates.chartexplorer = null;
      }

      // Three layers of URL validation with different scopes:
      //
      //   1. Structural (isValidModeForRoute, isValidCreateParam) — canvas
      //      routes only. Asks "is this param SHAPE valid on THIS route?"
      //      On sub-editor / settings / details routes the same params are
      //      passengers for round-trip restoration; structural strips here
      //      would trigger same-path setSearchParams navs that useBlocker
      //      intercepts as mid-session dirty exits → spurious alerts.
      //   2. Runtime-backing (isModeBackedByRuntime) — ALL routes. Asks
      //      "is there a live session backing these passengers?" Passengers
      //      are invisible to structural validation but NOT invisible to
      //      runtime-backing checks. Cold-start to /boxlayout/ with stale
      //      ?mode=edit&create=true must strip because no create flow is
      //      live. In-session passengers pass because fBuilderInfo or
      //      IsCustomChart is truthy at the time the gate fires.
      //   3. Permission (role/access-group checks above) — ALL routes.
      //
      // Pathname is basename-relative ("/base/ID") — same split index as
      // useRouteState.getResourceType, NOT coreapp's GetUIMode which reads
      // raw window.location.pathname and splits at a different index.
      const [, segment = ""] = pathname.split("/");
      const isCanvasActiveSegment = segment === "base" || segment === "open" || segment === "plan";
      if (isCanvasActiveSegment) {
        if (mode && !isValidModeForRoute(segment, mode, searchParams)) {
          updates.mode = null;
        }
        if (create === "true" && !isValidCreateParam(segment)) {
          updates.create = null;
        }
      }

      // Runtime-backing — ALL routes. isModeBackedByRuntime returns true
      // for any mode that has no runtime requirement; the edit+create
      // combo is the only universal gate today, and it applies regardless
      // of segment because the same passenger pair rides sub-editor and
      // settings routes. FinishLogin ensures /boxlayout/ cold-starts load
      // the default chart so IsCustomChart returns a meaningful answer.
      if (mode && !isModeBackedByRuntime(segment, mode, searchParams, GetWSMgr())) {
        updates.mode = null;
        // On non-canvas-active routes (/boxlayout/, /settings/, /details/),
        // ?create=true is a passenger for the round-trip — without a live
        // create flow backing it, the passenger is stale and would cause
        // spurious modal reopen on return to canvas. Strip it.
        // On canvas-active routes (/base/, /open/, /plan/), a lone
        // ?create=true without ?mode=edit is the INTENDED trigger for the
        // chart-builder modal (EditMenu.showCustomChartBuilderModal), so
        // leave it alone — the user asked to create a chart.
        if (create === "true" && !isCanvasActiveSegment) {
          updates.create = null;
        }
      }

      if (tool === "background" && doc.IsServerChart() && !perms.canEnterBackgroundEditor) {
        updates.tool = null;
      }

      // ?display=table is a planning-only tabular view; strip if user can't plan.
      if (display === "table" && !perms.canAccessPlanning) {
        updates.display = null;
      }
      if (display === "directory" && !perms.canAccessDirectory) {
        updates.display = null;
      }
      // ?display=viewmanager requires template-update permission (admins +
      // RW users with CanUpdateTemplates). Strips for RO or revoked users.
      if (display === "viewmanager" && !perms.canAccessTemplates) {
        updates.display = null;
      }

      // Per-view access check. Delegated to QPermissions.canAccessView —
      // single source of truth consumed here and in ViewManagerProvider.
      // Three-state semantic (null/empty → true, templates-not-loaded →
      // true, exists-but-not-allowed → false) keeps cold-start URLs from
      // being stripped before templates hydrate. The `chartDocument`
      // dependency above triggers re-check when templates load so the
      // strip fires on the next render rather than being dropped.
      const wsMgr = GetWSMgr();
      if (view && !wsMgr?.fPermissions?.canAccessView(view)) {
        updates.view = null;
      }

      if (Object.keys(updates).length === 0) return;

      setSearchParams(
        prev => {
          for (const key of Object.keys(updates)) {
            prev.delete(key);
          }
          return prev;
        },
        { replace: true }
      );
    } catch (err) {
      // Never brick the app on a permission check throw. Log and continue.
      // eslint-disable-next-line no-console
      console.error("[useUrlSecurityGate] permission check failed:", err);
    }
  }, [location.pathname, location.search, perms, searchParams, setSearchParams, chartDocument]);
};
